/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment, useEffect, useRef, useState, type ComponentType, type ReactElement } from 'react';
import { ChevronLeft, ChevronRight, Heart, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from '@/components/link';
import { BgMenuIcon, NmHamburgerIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth';
import { useBrand } from '@/lib/brand/use-brand';
import { HEADER_COMPOSITION } from '@/lib/brand/header-composition';
import { NAV_SALE_EMPHASIS, navSaleEmphasisAttr } from '@/lib/brand/nav-sale-emphasis';
import type { Brand } from '@/lib/brand/brand';
import type { NavContextKey, NavFlyout, NavLinkItem, NavSilo } from '@/lib/adapters/nav-content';
import { useNavContext } from './context';
import { targetHref } from './flyout';

/**
 * Mobile curated navigation: hamburger trigger + left-anchored overlay drawer,
 * fed by the SAME NavDocument as the desktop nav.
 *
 * ── TWO BRANDS, TWO DRAWERS ────────────────────────────────────────────────
 *
 * NM and BG do not render the same drawer with different numbers; they render
 * different MECHANISMS. NM drills in (a tapped silo REPLACES the list, and a
 * back button walks the levels); BG expands in place (a tapped silo opens a
 * sibling panel directly beneath its own row, and nothing is ever replaced).
 * No CSS value expresses that, so it is typed data — {@link DRAWER_COMPOSITION}
 * — in the established keyed-map idiom (`NAV_SWITCH` in ./index.tsx,
 * `HEADER_COMPOSITION` in lib/brand/header-composition.ts). A third brand is a
 * COMPILE ERROR there rather than a silent inheritance of NM's drawer.
 *
 * NM mechanics are production-faithful to the live-NM mobile drawer
 * (.reference/harvest/live-nm-mobile-drawer-structure.json); internal
 * dimensions from the follow-up micro-harvest
 * (.reference/harvest/micro-harvest-header2.md §4) via the --drawer-* tokens
 * in theme/tokens/header.css: 90vw width, 100px My Orders zone, 48px gender
 * tab row, exactly-47px silo rows with zero inter-row gaps, 30px label inset.
 * - fixed overlay, `--drawer-width` wide (live 337.5px at 375 = 90vw), full height, body
 *   scroll-locked while open, modal dialog semantics (Radix Sheet supplies
 *   role=dialog/aria-modal, focus trap, Escape + backdrop close, focus return);
 * - DRILL-IN panels, not an accordion: tapping a silo replaces the list with a
 *   child panel; a back button appears labeled after the parent level
 *   ("All Women" at level 2, the silo name at level 3) beside a highlighted
 *   context heading;
 * - gender tabs (WOMEN/MEN) and a My Orders row at the drawer top; the tabs
 *   swap the shared nav-content gender context (same state the desktop tabs
 *   drive) and reset drilling. Single-context documents (BG's `all`) omit the
 *   tabs — no gender dimension exists to swap.
 *
 * BG mechanics come from `.reference/captures/bg-drawer-375` (closed/top level)
 * and `bg-drawer-clothing-375` (one silo expanded), both taken at a 375 root:
 * - a 56px UTILITY TOP ROW carrying the close control on the LEFT and Sign in +
 *   Wishlist on the right (:329-341). That row is the only place BG mobile
 *   offers either control — its mobile header bar's right group opens with a
 *   literal empty `<div></div>` (bg-home-m-375:98) — so the top row is not
 *   decoration, it is the whole affordance;
 * - a flat 300px panel (:328 `max-w-[300px]`), not a viewport fraction;
 * - 46px rows built as a BOX: 15px pad + a 16px/16px label + 15px pad (:349),
 *   with NO row rule anywhere in the list (:346-357 carry no border class);
 * - the chevron slot LEADS the label (a 15x15 span at :350, before the text
 *   column), where NM's trails it;
 * - in-place expansion (bg-drawer-clothing-375:381-388): the slot gains
 *   `-rotate-90`, the label gains a black underline, and a sibling panel opens
 *   beneath the row at a 47px left inset. No slide, no second panel, no back
 *   affordance in either capture.
 *
 * Panel population is instant — the whole NavDocument is already client state
 * (recorded decision: we out-load live's async drawer shell,
 * docs/nm-migration-handoff.md "performance pathologies"). Panel changes are
 * instant swaps, no slide transition (mechanics over motion; 3b may add it).
 *
 * ── WHAT IS DELIBERATELY LEFT UNMEASURED ───────────────────────────────────
 *
 * - The close and chevron ARTWORK. Both are images in the captures and every
 *   asset was rehosted by the decompiler, so only their BOXES are readable
 *   (close 30x30 with 16/17/17/17 pads; chevron slot 15x15). Lucide glyphs
 *   stand in at the measured sizes.
 * - The silo label's FONT WEIGHT. RE-CHECKED 2026-08-19 in BOTH capture
 *   channels and the marker holds. The label node is
 *     h6.text-left.uppercase.text-[16px].leading-[16px]
 *       > span.text-left.uppercase          (bg-drawer-clothing-375:352-354)
 *   — size, leading and case, no weight. The file DOES emit weight classes
 *   elsewhere (10 of them, e.g. `font-bold` at :26 on the promo bar and :170
 *   on the hero), so this is a real absence on this element and not a
 *   decompiler that drops weights. The style channel cannot help either: it
 *   emits no `fontWeight` in any of the 77 captures, against a live positive
 *   control. So the weight below is the one this drawer already shipped —
 *   changing it would be inventing a value, not landing one.
 * - Whether more than one BG silo can be open at once. Every capture shows
 *   exactly one; single-open matches the evidence and nothing contradicts it.
 * - Open/close MOTION on BG, and the scrim's EXTENT (live's scrim occupies only
 *   `min-w-[calc(100%-300px)]` BESIDE the panel; ours is a full-viewport
 *   portal sibling). The extent is a restructure of ui/sheet.tsx's overlay,
 *   which the mini-cart and product-drawer issues own. Only the scrim COLOUR
 *   lands here.
 */

/**
 * How a tapped silo reveals its children.
 *
 * `drill-in`  — the panel's contents are REPLACED by the silo's own list and a
 *               back button walks back up (NM).
 * `accordion` — a sibling panel opens in place beneath the tapped row and the
 *               list around it stays put (BG, bg-drawer-clothing-375:388).
 */
type DrawerExpand = 'drill-in' | 'accordion';

/**
 * What the drawer does with a silo whose flyout leads with a LETTER INDEX —
 * the designers entry on both brands.
 *
 * `expand`   — the silo behaves like any other: it reveals its children
 *              (NM, which drills into a Designers panel).
 * `navigate` — the silo is a plain link to its own target and reveals
 *              nothing (BG, owner flicker 2026-08-23: "the designers link
 *              just goes to the designers page on live, we don't need to
 *              open sub options in designers a-z in the mobile menu").
 *
 * WHY THE LETTER INDEX IS THE DISCRIMINATOR AND NOT A SILO ID. Exactly one
 * silo per brand per context carries `flyout.letterIndex`, and on both brands
 * it is the designers entry targeting `/designers` — NM `cat000730` in both
 * `women` and `men`, BG `cat000001` in `all`. So the flyout's own shape names
 * this silo precisely, and no id has to be hardcoded into a composition map.
 * A brand whose document grows a second letter-index silo would pick up the
 * same behaviour, which is the intended reading rather than an accident.
 */
type DrawerLetterIndexSilo = 'expand' | 'navigate';

interface DrawerComposition {
    expand: DrawerExpand;
    letterIndexSilo: DrawerLetterIndexSilo;
    /**
     * Whether the drawer carries a utility top row with the close control,
     * Sign in and Wishlist.
     *
     * INVARIANT, pinned by this component's tests: this is true for exactly
     * the brands whose `HEADER_COMPOSITION[brand].mobileUtilities` is
     * `bag-only`. A brand that removes sign-in and wishlist from its mobile
     * header bar and does NOT put them in its drawer leaves a mobile shopper
     * no way to reach either but typing a URL, which is how this row came to
     * exist in the first place.
     */
    utilityTopRow: boolean;
    /**
     * Whether the drawer carries a dedicated "View Favorites" row at the bottom
     * of every panel.
     *
     * INVARIANT, pinned by this component's tests: this is true for exactly the
     * brands whose `HEADER_COMPOSITION[brand].mobileUtilities` is
     * `account-and-bag`. Those brands carry sign-in in the header bar and have
     * no wishlist affordance elsewhere on mobile — the drawer row is the only
     * way a shopper reaches their saved items.
     *
     * Live evidence:
     * - `.reference/captures/drawer-clothing-375/App.tsx:519-524` (level 2)
     * - `.reference/captures/drawer-open-old-375/App.tsx:494-499` (level 1)
     * Both show a `<li>` with a 15×18 heart icon and "View Favorites" text at the
     * bottom of every panel.
     */
    favoritesRow: boolean;
    /**
     * COMPLETE scrim class, or the empty string to keep the Sheet primitive's
     * own. Never a computed string — Tailwind's scanner has to see it.
     */
    scrimClass: string;
    /**
     * The glyph the hamburger trigger renders.
     *
     * A COMPONENT and not a class, because the two brands' marks are different
     * DRAWINGS rather than one drawing at two scales. Both are now live's own
     * asset: NM's is three filled rules at aspect 1.358, BG's a wider, shorter
     * three-rule stack at 1.774. `mobileMenuGlyphBox` in
     * lib/brand/header-composition.ts already carries each brand's BOX and
     * keeps doing so — the box positions and sizes the mark, this key chooses
     * which mark. Both are read one line apart at the trigger.
     *
     * NEITHER BRAND'S BOX MOVED WHEN ITS DRAWING LANDED. NM kept `size-5` and
     * BG kept live's declared 30px. That is the whole shape of this defect
     * class: a box-level comparison of either brand reported parity, because
     * the box was never the thing that was wrong.
     *
     * WHY THE KEY IS HERE AND NOT BESIDE `mobileMenuGlyphBox`. Two reasons, and
     * the second is the load-bearing one. Ownership: header-composition.ts was
     * held by another agent when this landed. Fit: that map is a map of CLASS
     * STRINGS and tokens, while this one is already the declared home for
     * "brand mechanism differences no CSS value expresses" — see this file's
     * header note and `expand` above. Consolidating the two keys into
     * HEADER_COMPOSITION later is a clean move and nothing here resists it.
     */
    mobileMenuGlyph: ComponentType<{ className?: string }>;
}

const DRAWER_COMPOSITION: Record<Brand, DrawerComposition> = {
    // nm: mobileMenuGlyph: `NmHamburgerIcon` — live NM's own asset, transcribed
    // byte-for-byte from the inline base64 on `img.mobile-navigation-hamburger-nav`
    // (alt `Navigation Menu`) in `.reference/homepage.mobile.html` and
    // `.reference/homepage.desktop.html`. Ink 20.00 x 14.67 at NM's unchanged
    // 20px box, against lucide `Menu`'s 15.33 x 13.33 in the same box, and
    // against live NM's scanned 20.0 x 14.5. See components/icons/nm-hamburger-icon.tsx
    // for the rule table and for the two independent instruments that agree
    // with it. THE BOX DID NOT MOVE — only the drawing did, which is why every
    // box-level check reported parity while the wrong mark was on screen.
    //
    // expand UNMEASURED. Both NM nav-drawer captures
    // (.reference/captures/drawer-clothing-375/App.tsx and drawer-open-old-375)
    // show the same UN-DRILLED root — gender tabs plus the flat silo list Clothing /
    // Dresses / Shoes / Handbags / Jewelry / Beauty / Accessories / Home / Vacation,
    // with no silo's children rendered and no back control anywhere in either file.
    // Nothing captured shows what an NM silo tap actually does, so 'drill-in' is our
    // choice, not a reading. utilityTopRow: false is not unmeasured — it is pinned
    // by the INVARIANT above against HEADER_COMPOSITION.nm.mobileUtilities.
    // favoritesRow: true is measured — both drawer captures end with a
    // "View Favorites" row (drawer-clothing-375:519-524, drawer-open-old-375:494-499).
    //
    // expand IS NOW MEASURED — the note above is retired. The owner's capture
    // live-shots/owner-2026-08-23-designers/nm-mobile-designers-menu.jpeg is a
    // real iPhone Safari shot whose address bar reads neimanmarcus.com, drilled
    // one level into Designers, and it shows the drill-in outright: the silo
    // list is GONE, a back row reading "< All Women" names the parent, and a
    // grey band carries the current section's name. That is a push, not an
    // accordion, so 'drill-in' is no longer our choice — it is a reading.
    //
    // letterIndexSilo: 'expand' — the same capture is the evidence. NM's
    // Designers entry DOES reveal children, so it keeps the drawer's ordinary
    // behaviour; only BG navigates.
    nm: {
        expand: 'drill-in',
        letterIndexSilo: 'expand',
        utilityTopRow: false,
        favoritesRow: true,
        scrimClass: '',
        mobileMenuGlyph: NmHamburgerIcon,
    },
    // Live BG's scrim is `bg-[rgb(54,_51,_69)] opacity-[0.5]`
    // (bg-drawer-375:327) — a violet-grey, not the primitive's black.
    // BG does NOT carry a separate favorites row — its wishlist link lives in the
    // utility top row alongside Sign In (bg-drawer-375:340).
    // mobileMenuGlyph: `BgMenuIcon` — live BG's own asset, transcribed
    // byte-for-byte from the inline base64 on `img.mobile-navigation-hamburger-nav`
    // in `.reference/bg-homepage.mobile.html` and three more BG dumps. Ink
    // 26.336 x 14.842 at BG's declared 30px box, against lucide `Menu`'s 23.0 x
    // 20.0 in the same box. See components/icons/bg-menu-icon.tsx for the rule
    // table and for the independent pixel scan that agrees with it.
    // letterIndexSilo: 'navigate' — owner flicker 2026-08-23, reported against
    // live BG mobile: the designers link goes straight to the designers page
    // and opens no sub options. Ours expanded an in-place panel of 48 links
    // (measured before the change: tapping `Designers A-Z` at 390 opened
    // `drawer-silo-panel-cat000001` and left the URL on the same page), so the
    // expansion was [ours-extra] and this key removes it.
    bg: {
        expand: 'accordion',
        letterIndexSilo: 'navigate',
        utilityTopRow: true,
        favoritesRow: false,
        scrimClass: 'bg-(--drawer-scrim-bg)',
        mobileMenuGlyph: BgMenuIcon,
    },
};

/** Drill path into the active context: [] = silo list, [siloId], [siloId, groupId]. */
type DrillPath = readonly string[];

/**
 * A row in a drawer panel: a leaf link, a drill-in trigger, or a non-linking
 * group heading whose members sit indented beneath it.
 *
 * `indent` on a leaf is the member-of-a-headed-group treatment, not a general
 * nesting level — nothing else in the drawer indents, and a leaf outside a
 * headed group never carries it.
 */
type DrawerRow =
    | { kind: 'leaf'; item: NavLinkItem; indent?: boolean }
    | { kind: 'drill'; id: string; label: string; emphasis?: 'sale' }
    | { kind: 'heading'; id: string; label: string };

interface DrawerPanel {
    /** Highlighted current-level name (context bar); undefined at the root. */
    heading?: string;
    /** Back-button label — the PARENT level's name (live: "All Women"). */
    backLabel?: string;
    rows: DrawerRow[];
}

/** One labelled run of links inside an expanded BG silo. */
interface AccordionSection {
    id: string;
    /** Grey uppercase run heading (bg-drawer-clothing-375:454). Plain text on
     *  live — the h6 wraps no anchor — so it is not a link here either. */
    heading?: string;
    links: NavLinkItem[];
}

function hasDrillContent(flyout?: NavFlyout): boolean {
    return Boolean(flyout && (flyout.columns?.length || flyout.letterIndex));
}

/**
 * Whether a silo REVEALS its children when tapped, as opposed to being a plain
 * link to its own target.
 *
 * Both the drill-in and the accordion branch ask this one question, so they
 * cannot disagree about a given silo: a brand that navigates its letter-index
 * silo does so at level 1 whichever mechanism it uses to reveal the others.
 *
 * Degrades correctly by construction: a silo with no letter index is decided
 * by `hasDrillContent` alone, exactly as before this key existed, so a brand
 * whose document carries no letter-index silo cannot be moved by either value.
 */
function siloExpands(silo: NavSilo, composition: DrawerComposition): boolean {
    if (silo.flyout?.letterIndex && composition.letterIndexSilo === 'navigate') {
        return false;
    }
    return hasDrillContent(silo.flyout);
}

/**
 * Flatten a silo's flyout into second-level rows, mirroring the live drawer's
 * level 2 (harvest secondLevelExample): a Shop All leaf for the silo's own
 * destination, the silo's subcategory links inline, and deeper drill rows for
 * the other headed groups (Dresses / Wear to / Trending…). The group whose
 * heading repeats the silo label IS the silo's own subcategory list — its
 * links inline as leaves rather than drilling into a redundant panel.
 */
function siloPanelRows(silo: NavSilo, shopAllLabel: string): DrawerRow[] {
    const rows: DrawerRow[] = [
        { kind: 'leaf', item: { id: `${silo.id}-shop-all`, label: shopAllLabel, target: silo.target } },
    ];
    // NO LETTER ROWS. The drilled panel does not reproduce the silo's letter
    // index — the owner's live capture of NM's drilled Designers panel
    // (live-shots/owner-2026-08-23-designers) runs the section heading straight
    // into a link to the A-Z page and then the featured designers, with not one
    // letter row between them. Ours emitted 28 of them, measured at 390 before
    // this change, which is [ours-extra]. The affordance is not lost: the leaf
    // above carries the silo's own target, which IS the A-Z page.
    //
    // SCOPE, stated because it is narrow. NM's only letter-index silo is
    // Designers, so this removes rows from exactly the panel the capture shows
    // and from no other. A silo without a letter index never reached this
    // branch. The accordion path builds its rows in siloAccordionSections and
    // is untouched, so BG keeps its letter runs wherever it still expands.
    // Last heading emitted, so a run split across desktop COLUMNS does not
    // repeat its heading here. Live's own nav payload carries the featured
    // designers as ONE group of 30 (dt-plp-1280-nm.html offset 1215507,
    // `"name":"Featured Designers"` with all 30 under it); the seed splits them
    // into `featured-1` / `featured-2` because live's DESKTOP flyout paints two
    // `grid-33` columns under one `grid-100` heading. The drawer has no
    // columns, so the split is a desktop artifact here and the heading is
    // emitted once for the run.
    let lastHeading: string | undefined;
    for (const column of silo.flyout?.columns ?? []) {
        for (const group of column.groups) {
            if (group.headingText) {
                // A NON-LINKING heading with its members indented beneath it —
                // live NM's drilled Designers panel, in the owner's capture
                // live-shots/owner-2026-08-23-designers. Not a drill row: the
                // members are right there under the heading in the same panel.
                if (group.headingText !== lastHeading) {
                    rows.push({ kind: 'heading', id: `${group.id}-heading`, label: group.headingText });
                    lastHeading = group.headingText;
                }
                for (const link of group.links) {
                    rows.push({ kind: 'leaf', item: link, indent: true });
                }
                continue;
            }
            lastHeading = undefined;
            if (group.heading && group.heading.label !== silo.label) {
                rows.push({
                    kind: 'drill',
                    id: group.id,
                    label: group.heading.label,
                    emphasis: group.heading.emphasis,
                });
            } else {
                for (const link of group.links) {
                    rows.push({ kind: 'leaf', item: link });
                }
            }
        }
    }
    return rows;
}

/**
 * Flatten a silo's flyout into the sections of ONE in-place accordion panel —
 * BG's shape, where there is no third level to drill into.
 *
 * bg-drawer-clothing-375:388-500 opens Clothing as: a `Shop All` list, then the
 * silo's own subcategory list, then a grey `Featured Designers` heading with
 * its list under it. Every headed group therefore becomes a heading + its
 * links IN THE SAME PANEL, where {@link siloPanelRows} turns the same group
 * into a row that drills one level deeper.
 *
 * No SYNTHETIC "Shop All" row is prepended here, unlike the drill-in path.
 * Live's first entry is the silo's own group's first link — its href is the
 * group's category (bg-drawer-clothing-375:392 `…/womens-clothing-cat441206`),
 * and the seed already carries that link at the head of the group whose
 * heading repeats the silo label. Adding one would duplicate it.
 */
function siloAccordionSections(silo: NavSilo): AccordionSection[] {
    const sections: AccordionSection[] = [];
    if (silo.flyout?.letterIndex) {
        sections.push({ id: `${silo.id}-letters`, links: silo.flyout.letterIndex.letters });
    }
    for (const column of silo.flyout?.columns ?? []) {
        for (const group of column.groups) {
            if (!group.links.length) {
                continue;
            }
            sections.push({
                id: group.id,
                // `headingText` first: it is already the plain string this
                // shape wants, and live's accordion headings wrap no anchor
                // either. No BG group carries one today, so this arm is inert
                // on the only brand that takes the accordion path.
                heading:
                    group.headingText ??
                    (group.heading && group.heading.label !== silo.label ? group.heading.label : undefined),
                links: group.links,
            });
        }
    }
    return sections;
}

export default function CuratedMobileNav(): ReactElement | null {
    const nav = useNavContext();
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    const session = useAuth();
    const brand = useBrand();
    const [open, setOpen] = useState(false);
    const [path, setPath] = useState<DrillPath>([]);
    // BG only — the silo whose sibling panel is open. `null` = all collapsed.
    // Single-open because every capture shows exactly one expanded silo; see
    // the unmeasured list in this file's header.
    const [expandedSilo, setExpandedSilo] = useState<string | null>(null);
    // Focus target after a panel swap: drilling unmounts the tapped row, which
    // would drop focus to <body> and break the tab order inside the dialog.
    const panelRef = useRef<HTMLElement>(null);
    const pathKey = path.join('/');

    useEffect(() => {
        if (open && pathKey) {
            panelRef.current?.focus();
        }
    }, [open, pathKey]);

    if (!nav) {
        return null;
    }

    const headerComposition = HEADER_COMPOSITION[brand];
    const drawerComposition = DRAWER_COMPOSITION[brand];
    // Capitalised binding because JSX reads a lowercase tag as a DOM element.
    const MobileMenuGlyph = drawerComposition.mobileMenuGlyph;
    const isAccordion = drawerComposition.expand === 'accordion';
    // Whether this brand emphasises `Sale` IN THE DRAWER — NM does, BG does
    // not, and on the desktop cat nav it is the other way round. The colour is
    // applied by an unlayered rule in theme/overrides/navigation.css keyed on
    // the attribute this produces; a utility class cannot be used, see the
    // comment on that rule. Returns undefined for every non-sale row and for
    // every row on a brand that does not emphasise here, so the attribute is
    // simply absent rather than present-and-falsy.
    const saleEmphasisAttr = (emphasis: string | undefined) =>
        navSaleEmphasisAttr(NAV_SALE_EMPHASIS[brand].drawer, emphasis);

    const { activeContext, contextKey, setContextKey, document: navDocument } = nav;
    const contextKeys = Object.keys(navDocument.contexts) as NavContextKey[];
    const showGenderTabs = contextKeys.length > 1;

    const genderLabels: Partial<Record<NavContextKey, string>> = {
        women: t('genderWomen', { defaultValue: 'Women' }),
        men: t('genderMen', { defaultValue: 'Men' }),
    };
    // Back label at level 2 — "All Women" / "All Men" (harvest
    // secondLevelExample.backButton). Single-context documents have no gender
    // word to append; they fall back to the bare "All".
    const contextLabel = genderLabels[contextKey];
    const rootBackLabel = contextLabel
        ? t('drawerBackAll', { context: contextLabel, defaultValue: `All ${contextLabel}` })
        : t('drawerBackRoot', { defaultValue: 'All' });

    const silo = path.length >= 1 ? activeContext.silos.find((s) => s.id === path[0]) : undefined;
    const group =
        silo && path.length >= 2
            ? silo.flyout?.columns?.flatMap((column) => column.groups).find((g) => g.id === path[1])
            : undefined;

    let panel: DrawerPanel;
    if (silo && group?.heading) {
        // Level 3 — a headed group's own panel; back returns to the silo.
        panel = {
            heading: group.heading.label,
            backLabel: silo.label,
            rows: [
                {
                    kind: 'leaf',
                    item: {
                        id: `${group.id}-shop-all`,
                        label: t('drawerShopAll', { defaultValue: 'Shop All' }),
                        target: group.heading.target,
                    },
                },
                ...group.links.map((link): DrawerRow => ({ kind: 'leaf', item: link })),
            ],
        };
    } else if (silo) {
        // Level 2 — the silo's panel; back returns to the first level.
        panel = {
            heading: silo.label,
            backLabel: rootBackLabel,
            // The silo's own value wins over the shared key. `drawerShopAll` is
            // used by every silo on both brands, so renaming it for NM's
            // Designers panel would rename Clothing's row and every other one —
            // see NavSilo.shopAllLabel.
            rows: siloPanelRows(silo, silo.shopAllLabel ?? t('drawerShopAll', { defaultValue: 'Shop All' })),
        };
    } else {
        // Level 1 — the silo list.
        panel = {
            rows: activeContext.silos.map((s): DrawerRow => {
                return siloExpands(s, drawerComposition)
                    ? { kind: 'drill', id: s.id, label: s.label, emphasis: s.emphasis }
                    : { kind: 'leaf', item: s };
            }),
        };
    }

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            setPath([]);
            setExpandedSilo(null);
        }
    };

    const close = () => handleOpenChange(false);

    const selectContext = (key: NavContextKey) => {
        setContextKey(key);
        setPath([]);
        setExpandedSilo(null);
    };

    // Silo/panel row rhythm (mh2 §4): every row exactly 47px (--drawer-row-height,
    // border-inclusive: 15px pad + 16px label + 15px pad + 1px border) with zero
    // inter-row gaps; the li/button is full-bleed so the bottom border spans edge
    // to edge, the label insets 30px left (--drawer-label-inset) and the chevron
    // right-aligns inside the 20px right padding.
    const rowClasses =
        'flex h-(--drawer-row-height) w-full items-center justify-between gap-2 border-b border-header-border pl-(--drawer-label-inset) pr-5 text-left text-base font-medium hover:opacity-70 transition-opacity focus-ring-button';

    // BG's row is a BOX, not a fixed height (bg-drawer-375:349
    // `pt-[15px] pr-0 pb-[15px] pl-0` around a 16px/16px label = 46px), and it
    // carries no rule: nothing in :346-357 emits a border. Reproducing the
    // decomposition rather than collapsing it onto one `h-` keeps the label's
    // own leading the thing that sets the row height, exactly as live does.
    const bgRowClasses = 'flex w-full items-center py-[15px] pl-0 text-left hover:opacity-70 transition-opacity';
    // 16px/16px uppercase (:352). The WEIGHT is deliberately the one this
    // drawer already shipped — the captures emit no weight for this label.
    const bgLabelClasses = 'block text-base leading-4 font-medium uppercase';
    // Leaf rows drop the chevron slot and inset the label by 16px instead
    // (bg-drawer-375:374 `ml-[16px]`).
    const bgLeafLabelClasses = `${bgLabelClasses} ml-4`;

    // Sign in / Wishlist destinations are the ones the header bar used before
    // BG's mobile recomposition removed both controls from it — same auth-aware
    // pair as user-actions/user-actions.tsx and header/wishlist-icon.tsx, so a
    // signed-in BG shopper still lands on the account/wishlist pages rather
    // than the guest ones.
    const isAuthenticated = session?.userType === 'registered' && Boolean(session?.customerId);
    const accountLink = isAuthenticated ? '/account/overview' : '/login';
    const accountLabel = isAuthenticated ? tAccount('myAccount') : t('signIn');
    const wishlistLink = isAuthenticated ? '/account/wishlist' : '/wishlist';

    // Favorites row — NM mobile only
    // (drawer-clothing-375:519-524; drawer-open-old-375:494-499).
    // The row sits at the bottom of EVERY panel: level 1 (root silo list) and
    // levels 2 and 3 (drilled-in panels). Both captures show it with a 15×18
    // heart icon, a "View Favorites" label, `mb-[30px]` below the li, and
    // `pt-[15px] pb-[15px] pl-(--drawer-label-inset)` matching regular rows.
    const favoritesRowEl = drawerComposition.favoritesRow ? (
        <ul>
            <li className="mb-[30px]">
                <Link
                    to={wishlistLink}
                    onClick={close}
                    data-testid="drawer-favorites"
                    className="flex h-(--drawer-row-height) w-full items-center gap-2 border-b border-header-border pl-(--drawer-label-inset) pr-5 text-left text-base font-medium hover:opacity-70 transition-opacity focus-ring-button">
                    <Heart aria-hidden className="size-[15px] shrink-0" style={{ height: '18px', width: '15px' }} />
                    {t('drawerViewFavorites', { defaultValue: 'View Favorites' })}
                </Link>
            </li>
        </ul>
    ) : null;

    const navBody = (
        <nav
            ref={panelRef}
            tabIndex={-1}
            aria-label={t('mobileNavigation', 'Mobile navigation menu')}
            className="outline-none">
            {isAccordion ? (
                <ul>
                    {activeContext.silos.map((s) => {
                        const expandable = siloExpands(s, drawerComposition);
                        const isExpanded = expandedSilo === s.id;
                        if (!expandable) {
                            return (
                                <li key={`leaf-${s.id}`}>
                                    <DrawerLeafLink
                                        item={s}
                                        saleEmphasis={saleEmphasisAttr(s.emphasis)}
                                        onNavigate={close}
                                        className={cn(bgRowClasses, 'focus-ring-button')}
                                        labelClassName={bgLeafLabelClasses}
                                    />
                                </li>
                            );
                        }
                        return (
                            <li key={`silo-${s.id}`}>
                                <button
                                    type="button"
                                    aria-expanded={isExpanded}
                                    data-emphasis={s.emphasis}
                                    data-sale-emphasis={saleEmphasisAttr(s.emphasis)}
                                    data-testid={`drawer-silo-${s.id}`}
                                    onClick={() => setExpandedSilo(isExpanded ? null : s.id)}
                                    className={cn(bgRowClasses, 'focus-ring-button')}>
                                    {/* 15x15 slot, chevron LEADING the label
                                        (:350). `-rotate-90` when open is live's
                                        own expanded-state class
                                        (bg-drawer-clothing-375:381); the glyph
                                        inside it is unmeasured artwork. */}
                                    <span
                                        aria-hidden
                                        className={cn(
                                            'flex size-[15px] shrink-0 items-center justify-center',
                                            isExpanded && '-rotate-90'
                                        )}>
                                        <ChevronRight className="size-[15px]" />
                                    </span>
                                    <span className="w-[calc(100%-20px)]">
                                        <span
                                            className={cn(
                                                bgLabelClasses,
                                                // Expanded rows underline the
                                                // label in black (:383). The
                                                // rule spans the whole text
                                                // column, as live's does — the
                                                // border sits on the block-level
                                                // heading, not on the glyphs.
                                                isExpanded && 'border-b border-(--drawer-expanded-rule-color) pb-1'
                                            )}>
                                            {s.label}
                                        </span>
                                    </span>
                                </button>
                                {isExpanded && (
                                    <div
                                        data-testid={`drawer-silo-panel-${s.id}`}
                                        // 47px left inset (:388) with the run
                                        // wrapper insetting 44px from the right
                                        // and closing on a black rule (:389).
                                        className="relative pl-[47px]">
                                        <div className="mt-5 mr-[44px] mb-5 border-b border-(--drawer-expanded-rule-color)">
                                            {siloAccordionSections(s).map((section) => (
                                                <Fragment key={section.id}>
                                                    {section.heading && (
                                                        <p className="pt-5 pb-[25px] text-[13px] text-(--drawer-group-heading-color) uppercase">
                                                            {section.heading}
                                                        </p>
                                                    )}
                                                    <ul className="mb-5">
                                                        {section.links.map((link, index) => (
                                                            // 30px run rhythm,
                                                            // 13px leading, the
                                                            // first anchor of
                                                            // each run opening
                                                            // on 20px (:391-395).
                                                            <li key={link.id} className="pb-[30px] leading-[13px]">
                                                                <DrawerLeafLink
                                                                    item={link}
                                                                    onNavigate={close}
                                                                    saleEmphasis={saleEmphasisAttr(link.emphasis)}
                                                                    className={cn(
                                                                        'block text-[13px] hover:opacity-70 transition-opacity focus-ring-button',
                                                                        index === 0 && 'pt-5'
                                                                    )}
                                                                />
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </Fragment>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <Fragment>
                    <ul>
                        {/* Keys are namespaced by row kind: the seed reuses a
                            subcategory link id as its drill-group id (e.g.
                            Clothing's "Dresses"), so bare ids collide across
                            leaf and drill rows — duplicate keys let a stale
                            row leak through panel swaps. */}
                        {panel.rows.map((row) => {
                            if (row.kind === 'drill') {
                                return (
                                    <li key={`drill-${row.id}`}>
                                        <button
                                            onClick={() => setPath([...path, row.id])}
                                            data-emphasis={row.emphasis}
                                            data-sale-emphasis={saleEmphasisAttr(row.emphasis)}
                                            className={rowClasses}>
                                            {row.label}
                                            <ChevronRight aria-hidden className="size-5 shrink-0" />
                                        </button>
                                    </li>
                                );
                            }
                            if (row.kind === 'heading') {
                                // Plain text, no anchor and no chevron — live's
                                // is an `h6.featured-heading` wrapping nothing,
                                // and its nav payload gives the entry an empty
                                // url. A `<p>` rather than a heading element
                                // because the drawer's own section band already
                                // holds the panel's heading role.
                                return (
                                    <li key={`heading-${row.id}`}>
                                        <p
                                            data-testid={`drawer-group-heading-${row.id}`}
                                            className={cn(rowClasses, 'justify-start')}>
                                            {row.label}
                                        </p>
                                    </li>
                                );
                            }
                            return (
                                <li key={`leaf-${row.item.id}`}>
                                    <DrawerLeafLink
                                        item={row.item}
                                        onNavigate={close}
                                        className={cn(
                                            rowClasses,
                                            // One level of indent under a group
                                            // heading. APPROXIMATE: derived by
                                            // ratio off the owner's live photo
                                            // (live-shots/owner-2026-08-23-designers),
                                            // where the heading's own left edge
                                            // is 63 image px and a member's is
                                            // 83. Scaled against the 30px
                                            // `--drawer-label-inset` that was
                                            // measured off live's own drawer
                                            // captures, that is 39.5 -> a ~10px
                                            // step; two independent scalings
                                            // from the image width give 8.3 and
                                            // 9.1, so the band is 8-10 and the
                                            // exact value is unmeasured.
                                            row.indent && 'pl-[calc(var(--drawer-label-inset)+10px)]'
                                        )}
                                        saleEmphasis={saleEmphasisAttr(row.item.emphasis)}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                    {favoritesRowEl}
                </Fragment>
            )}
        </nav>
    );

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    // Brand-scoped BUTTON BOX — `mobileMenuBox` in
                    // lib/brand/header-composition.ts. `size="icon"` is shadcn's
                    // own `size-9`, a UI-kit default nobody measured for either
                    // brand; NM's live control is 42 x 28 (home-m-390:84
                    // `w-[42px] h-7`, corroborated by
                    // .reference/homepage.mobile.styles.json -> hamburger) and
                    // takes it, BG's is unmeasured and its empty string keeps
                    // the stock box byte-identically. Appended AFTER the base
                    // string so tailwind-merge resolves `size-9` away against
                    // NM's `w-`/`h-` pair and leaves BG's untouched.
                    //
                    // THE BOX IS NOT THE GLYPH. The child below is the ink and
                    // is already correct on both brands; nothing here changes
                    // it, and the two must not be collapsed onto one value —
                    // that is the mistake the field docs record.
                    className={cn(
                        'hover:bg-transparent hover:opacity-50 transition-opacity',
                        headerComposition.mobileMenuBox
                    )}
                    aria-label={t('openMenu', 'Open menu')}>
                    {/* Brand-scoped box — `mobileMenuGlyphBox` in
                        lib/brand/header-composition.ts carries each brand's
                        measurement and the reason they cannot share one. NM's
                        value is `size-5` and BG's live-declared 30px; neither
                        moved when the drawings landed.

                        Brand-scoped GLYPH — `mobileMenuGlyph` in
                        DRAWER_COMPOSITION above. The box could never have
                        carried this: the two brands' marks differ in ASPECT,
                        1.358 for NM against 1.774 for BG, and the lucide mark
                        both brands used to share inks at ~1.15 whatever box it
                        is given. Each is now live's own asset, transcribed in
                        components/icons/nm-hamburger-icon.tsx and
                        components/icons/bg-menu-icon.tsx. */}
                    <MobileMenuGlyph className={headerComposition.mobileMenuGlyphBox} />
                </Button>
            </SheetTrigger>
            {open && (
                <SheetContent
                    side="left"
                    data-testid="curated-nav-mobile"
                    // Styling hook for theme/overrides/navigation.css, which
                    // scopes the emphasised-`Sale` rule to this surface. A
                    // semantic surface name rather than the test id beside it:
                    // data-testid is a test handle and stylesheets keying off
                    // it make it load-bearing for rendering.
                    data-surface="nav-drawer"
                    aria-modal="true"
                    aria-describedby={undefined}
                    overlayClassName={drawerComposition.scrimClass || undefined}
                    // The primitive's own top-RIGHT close is suppressed only on
                    // brands that render their own; every other surface keeps it.
                    closeButton={drawerComposition.utilityTopRow ? null : undefined}
                    className={
                        drawerComposition.utilityTopRow
                            ? // The top row must not scroll away with the list,
                              // so the panel stops scrolling and the list below
                              // it scrolls instead — the flex equivalent of
                              // live's fixed 56px row over an `overflow-auto`
                              // body (bg-drawer-375:329, :344).
                              'w-(--drawer-width) max-w-full gap-0 overflow-hidden bg-header-background text-header-foreground'
                            : 'w-(--drawer-width) max-w-full gap-0 overflow-y-auto bg-header-background text-header-foreground'
                    }>
                    <SheetTitle className="sr-only">{t('drawerTitle', { defaultValue: 'Menu' })}</SheetTitle>

                    {/* Utility top row — the close control on the LEFT and the
                        brand's account + wishlist affordances on the right
                        (bg-drawer-375:329-341). 56px of CONTENT under a 1px top
                        rule, hence `box-content`: live's row is
                        `border-t box-content … h-14`, which is why its body
                        starts at 57px and not 56. */}
                    {drawerComposition.utilityTopRow && (
                        <div
                            data-testid="drawer-utility-row"
                            className="box-content flex h-14 shrink-0 items-center justify-between border-t border-t-(--drawer-top-row-border-color)">
                            <SheetClose
                                aria-label={t('closeMenu', { defaultValue: 'Close menu' })}
                                data-testid="drawer-close"
                                // `self-start`, not the row's centring: live's
                                // control is `float-left` in a 56px row and its
                                // padded box is 63px tall, so it starts at the
                                // row's content top and overhangs the bottom.
                                // Centring it instead moves the glyph 2.5px up
                                // from where live paints it.
                                className="self-start hover:opacity-70 transition-opacity focus-ring-button">
                                {/* 30x30 with 16/17/17/17 pads (:331). The
                                    artwork itself is unmeasured — the capture's
                                    asset was rehosted by the decompiler. */}
                                <X aria-hidden className="box-content size-[30px] pt-4 pr-[17px] pb-[17px] pl-[17px]" />
                            </SheetClose>
                            <div className="flex items-center">
                                {/* 20px side pads, 17.94px leading, 13px
                                    inherited from the app root (:335-340
                                    with :6). */}
                                <Link
                                    to={accountLink}
                                    onClick={close}
                                    data-testid="drawer-sign-in"
                                    className="px-5 text-[13px] leading-[17.94px] hover:opacity-70 transition-opacity focus-ring-button">
                                    {accountLabel}
                                </Link>
                                <Link
                                    to={wishlistLink}
                                    onClick={close}
                                    data-testid="drawer-wishlist"
                                    className="px-5 text-[13px] leading-[17.94px] hover:opacity-70 transition-opacity focus-ring-button">
                                    {t('wishlist')}
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* Account row — drawer top, above the gender tabs (harvest
                        "account row": only My Orders in the signed-out state).
                        mh2 §4: a 100px-tall zone (--drawer-orders-height) whose
                        link text sits top-left (~y28, x~30) with dead space
                        below — the height is the zone's, not the link's.
                        Gated on the SAME brand row as the header bar's My
                        Orders link (header/index.tsx): a brand whose header
                        drops the link must not have its drawer re-add it, and
                        live BG's drawer is close + sign in + wishlist and
                        nothing else (bg-drawer-375:329-341). */}
                    {headerComposition.myOrders && (
                        <div className="h-(--drawer-orders-height) border-b border-header-border px-(--drawer-label-inset) pt-7">
                            <NavLink
                                to="/account/orders"
                                data-testid="drawer-my-orders"
                                onClick={close}
                                className="type-label-small hover:opacity-70 transition-opacity">
                                {t('myOrders', { defaultValue: 'My Orders' })}
                            </NavLink>
                        </div>
                    )}

                    {/* Gender tabs INSIDE the drawer — swap the shared nav
                        context, exactly like the desktop tabs. Omitted for
                        single-context documents (BG's `all`). */}
                    {showGenderTabs && (
                        // mh2 §4: 48px tab row (--drawer-tabs-height); the tab
                        // labels start at the drawer's 30px text inset (live
                        // WOMEN button x=31).
                        <nav
                            aria-label={t('genderNavLabel', { defaultValue: 'Shop by category' })}
                            data-testid="drawer-gender-tabs"
                            className="flex h-(--drawer-tabs-height) items-center gap-8 border-b border-header-border px-(--drawer-label-inset)">
                            {(['women', 'men'] as const)
                                .filter((key) => key in navDocument.contexts)
                                .map((key) => (
                                    <button
                                        key={key}
                                        onClick={() => selectContext(key)}
                                        aria-pressed={contextKey === key}
                                        data-testid={`drawer-gender-tab-${key === 'men' ? 'mens' : 'womens'}`}
                                        className={cn(
                                            'type-label-large uppercase cursor-pointer pb-1 border-b-4 focus-ring-button',
                                            contextKey === key
                                                ? 'border-header-foreground'
                                                : 'border-transparent hover:opacity-50'
                                        )}>
                                        {genderLabels[key]}
                                    </button>
                                ))}
                        </nav>
                    )}

                    {/* Context bar — empty at the first level; back button +
                        highlighted current name after drilling (harvest
                        "context bar": .mobilenav__backbutton + .parent-cat-highlighted). */}
                    {path.length > 0 && (
                        // TWO BANDS, not one row. Live's drilled panel stacks a
                        // white back row over a GREY band carrying the current
                        // section's name, each spanning the drawer's full width
                        // and each with its own rule. Ours used to put both on
                        // ONE row with the heading pushed right by `ml-auto`,
                        // which is the shape the owner reported as "different
                        // from how we have it". Measured at 390 before this
                        // change: drawer-back and drawer-context-heading shared
                        // a y of -33 and a height of 20, and the heading's
                        // background computed rgba(0, 0, 0, 0).
                        //
                        // Both bands adopt the drawer's 30px text inset so their
                        // labels align with the silo rows above and below.
                        <Fragment>
                            <div className="border-b border-header-border px-(--drawer-label-inset) py-3">
                                <button
                                    data-testid="drawer-back"
                                    onClick={() => setPath(path.slice(0, -1))}
                                    className="flex items-center gap-1 type-label-medium uppercase hover:opacity-70 transition-opacity focus-ring-button">
                                    <ChevronLeft aria-hidden className="size-4" />
                                    {panel.backLabel}
                                </button>
                            </div>
                            {/* The grey ground is a BRAND token read with a
                                `transparent` fallback, so it is inert by
                                construction on any brand that has not declared
                                it — no shared default is invented for a value
                                only NM was measured for. The class is written
                                out in full because Tailwind's scanner has to
                                see the literal. */}
                            <div
                                data-testid="drawer-section-band"
                                className="border-b border-header-border bg-[var(--drawer-section-heading-bg,transparent)] px-(--drawer-label-inset) py-3">
                                <span data-testid="drawer-context-heading" className="type-label-medium font-semibold">
                                    {panel.heading}
                                </span>
                            </div>
                        </Fragment>
                    )}

                    {/* Full-bleed panel: zero padding (mh2 §4 — ul/nav padding: 0)
                        so the rows' bottom borders span edge to edge and rows
                        stack with zero gaps; the text inset lives on each row,
                        not on the panel. Brands with a fixed top row scroll the
                        LIST rather than the whole panel. */}
                    {drawerComposition.utilityTopRow ? (
                        <div className="min-h-0 flex-1 overflow-y-auto">{navBody}</div>
                    ) : (
                        navBody
                    )}
                </SheetContent>
            )}
        </Sheet>
    );
}

function DrawerLeafLink({
    item,
    onNavigate,
    className,
    labelClassName,
    saleEmphasis,
}: {
    item: NavLinkItem;
    onNavigate: () => void;
    className?: string;
    /**
     * When set, the label is wrapped in a span carrying these classes instead
     * of being the anchor's bare text child. Omitted (the default) renders the
     * bare text child every existing caller already gets.
     */
    labelClassName?: string;
    /**
     * Resolved `data-sale-emphasis` value from the caller's brand row, or
     * undefined. Passed in rather than derived here because the decision is
     * per BRAND and per SURFACE (lib/brand/nav-sale-emphasis.ts) and this
     * component knows neither.
     */
    saleEmphasis?: 'bold' | 'plain';
}): ReactElement {
    const classes = className;
    const label = labelClassName ? <span className={labelClassName}>{item.label}</span> : item.label;
    if (item.target.type !== 'category' && item.target.href.startsWith('#')) {
        return (
            <a
                href={item.target.href}
                className={classes}
                data-emphasis={item.emphasis}
                data-sale-emphasis={saleEmphasis}>
                {label}
            </a>
        );
    }
    return (
        <NavLink
            to={targetHref(item.target)}
            onClick={onNavigate}
            className={classes}
            data-emphasis={item.emphasis}
            data-sale-emphasis={saleEmphasis}>
            {label}
        </NavLink>
    );
}

// eslint-disable-next-line react-refresh/only-export-components -- typed brand row co-located with the component it configures
export { DRAWER_COMPOSITION };
